export interface TrickplayTileSegment {
  startTime: number;
  duration: number;
  tileDuration: number;
  url: string;
  width: number;
  height: number;
  cols: number;
  rows: number;
}

export interface TrickplayTrack {
  segments: TrickplayTileSegment[];
  placeholder: boolean;
}

export interface TrickplayFrame {
  tileUrl: string;
  width: number;
  height: number;
  cols: number;
  rows: number;
  col: number;
  row: number;
}

function parseAttributes(line: string): Record<string, string> {
  const rawAttributes = line.slice(line.indexOf(":") + 1);
  return rawAttributes.split(",").reduce<Record<string, string>>((acc, entry) => {
    const [key, ...rest] = entry.split("=");
    if (!key || rest.length === 0) {
      return acc;
    }
    acc[key.trim()] = rest.join("=").trim().replace(/^"|"$/g, "");
    return acc;
  }, {});
}

export function parseTrickplayImagePlaylist(manifest: string, manifestURL: string): TrickplayTrack {
  const lines = manifest
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const segments: TrickplayTileSegment[] = [];
  let cursorTime = 0;
  let placeholder = lines.some((line) => line.startsWith("#EXT-X-KNOTFIN-PLACEHOLDER:"));

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!;
    if (!line.startsWith("#EXTINF:")) {
      continue;
    }

    const duration = Number.parseFloat(line.slice("#EXTINF:".length).replace(/,$/, ""));
    const tilesLine = lines[index + 1];
    const tileURL = lines[index + 2];
    if (!tilesLine?.startsWith("#EXT-X-TILES:") || !tileURL || tileURL.startsWith("#")) {
      continue;
    }

    const attributes = parseAttributes(tilesLine);
    const resolution = (attributes.RESOLUTION ?? "").split("x").map((value) => Number.parseInt(value, 10));
    const layout = (attributes.LAYOUT ?? "").split("x").map((value) => Number.parseInt(value, 10));
    const tileDuration = Number.parseFloat(attributes.DURATION ?? "0");
    const width = Number.isFinite(resolution[0]) ? resolution[0]! : 0;
    const height = Number.isFinite(resolution[1]) ? resolution[1]! : 0;
    const cols = Number.isFinite(layout[0]) ? layout[0]! : 0;
    const rows = Number.isFinite(layout[1]) ? layout[1]! : 0;

    if (duration <= 0 || tileDuration <= 0 || width <= 0 || height <= 0 || cols <= 0 || rows <= 0) {
      continue;
    }

    const resolvedURL = new URL(tileURL, manifestURL).toString();
    if (!placeholder) {
      placeholder = new URL(resolvedURL).searchParams.get("state") === "pending";
    }

    segments.push({
      startTime: cursorTime,
      duration,
      tileDuration,
      url: resolvedURL,
      width,
      height,
      cols,
      rows,
    });
    cursorTime += duration;
  }

  if (segments.length === 0) {
    throw new Error("No trickplay image tiles were found");
  }

  return { segments, placeholder };
}

export function locateTrickplayFrame(track: TrickplayTrack | null, timeSeconds: number): TrickplayFrame | null {
  if (!track || !Number.isFinite(timeSeconds) || timeSeconds < 0) {
    return null;
  }

  const segment = track.segments.find((candidate) =>
    timeSeconds >= candidate.startTime && timeSeconds < candidate.startTime + candidate.duration,
  ) ?? track.segments[track.segments.length - 1];
  if (!segment) {
    return null;
  }

  const frameIndex = Math.max(
    0,
    Math.min(
      Math.floor((timeSeconds - segment.startTime) / segment.tileDuration),
      (segment.cols * segment.rows) - 1,
    ),
  );
  return {
    tileUrl: segment.url,
    width: segment.width,
    height: segment.height,
    cols: segment.cols,
    rows: segment.rows,
    col: frameIndex % segment.cols,
    row: Math.floor(frameIndex / segment.cols),
  };
}

export function sliderPreviewTimeFromPointer(clientX: number, rect: DOMRect, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0 || rect.width <= 0) {
    return 0;
  }
  const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  return ratio * duration;
}

export function sliderPreviewOffsetFromPointer(clientX: number, rect: DOMRect): number {
  if (rect.width <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(rect.width, clientX - rect.left));
}

export function computePreviewPanelPosition(pointerOffsetPx: number, railWidthPx: number, panelWidthPx: number): {
  leftPx: number;
  pointerWithinPanelPx: number;
} {
  if (railWidthPx <= 0 || panelWidthPx <= 0) {
    return { leftPx: 0, pointerWithinPanelPx: 0 };
  }
  const clampedPointerPx = Math.max(0, Math.min(railWidthPx, pointerOffsetPx));
  const maxLeftPx = Math.max(0, railWidthPx - panelWidthPx);
  const leftPx = Math.max(0, Math.min(clampedPointerPx - panelWidthPx / 2, maxLeftPx));
  return {
    leftPx,
    pointerWithinPanelPx: Math.max(0, Math.min(panelWidthPx, clampedPointerPx - leftPx)),
  };
}
